import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, input, viewChildren } from '@angular/core';
import { BreakpointObserverService, HeaderMenuService, NavigationService } from '@core';
import { NgxSkeletonLoaderConfigTheme, NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';

import { INavigationTree } from '../../interfaces/navigation-tree.interface';
import { NavigationLinksGroupComponent } from '../navigation-links-group/navigation-links-group.component';
import { NavigationLinksMobileGroupComponent } from '../navigation-links-mobile-group/navigation-links-mobile-group.component';
import { NavigationMenuService } from '../navigation-menu-mobile/navigation-menu.service';

@Component({
  selector: 'lib-navigation-links',
  templateUrl: './navigation-links.component.html',
  styleUrls: ['./navigation-links.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NavigationLinksGroupComponent, NavigationLinksMobileGroupComponent, NgxSkeletonLoaderModule],
})
export class NavigationLinksComponent {
  readonly navigationTree = input<INavigationTree[]>();
  readonly headerTitle = input.required<string>();
  readonly APPLE_CATEGORY_ID = 60001;
  readonly APPLE_BRAND_ID = 1032;

  readonly menuItem = viewChildren<ElementRef>('menuItem');

  isTabletOrLower = inject(BreakpointObserverService).isTabletOrLower;
  isDesktopOrBigger = inject(BreakpointObserverService).isDesktopOrBigger;
  navigationMenuMobileService = inject(NavigationMenuService);
  navigationService = inject(NavigationService);
  headerMenuService = inject(HeaderMenuService);

  readonly skeletonConfig: NgxSkeletonLoaderConfigTheme = {
    height: '35px',
    width: '100%',
    border: '1px solid white',
  };

  readonly vm = computed(() => {
    const activeItem = this.navigationMenuMobileService.activeTreeItem();

    return {
      navigationTree: this.navigationTree(),
      activeTreeItem: activeItem,
      headerTitle: this.headerTitle(),
      isActiveTreeItemGroup: !!activeItem?.group?.length,
      isTabletOrLower: this.isTabletOrLower(),
      isDesktopOrBigger: this.isDesktopOrBigger(),
      selectedCategory: this.navigationMenuMobileService.activeTreeItem(),
    };
  });

  onSelectActiveTreeItem(activeTreeItem: INavigationTree | null): void {
    this.navigationMenuMobileService.activeTreeItem.set(activeTreeItem);
  }

  onMouseEnterTreeItem(item: INavigationTree): void {
    this.onSelectActiveTreeItem(item);
  }

  onKeyboardSelectTreeItem(item: INavigationTree): void {
    this.onSelectActiveTreeItem(item);
  }

  onFocusOutsideEvent(): void {
    const list = [...this.menuItem()];
    const nextIndex = list.findIndex((item) => item.nativeElement.classList.contains('active')) + 1;

    if (nextIndex < list.length) {
      list[nextIndex].nativeElement.focus();
    }
  }

  onShopAll(): void {
    const activeItem = this.navigationMenuMobileService.activeTreeItem();
    if (!activeItem) {
      return;
    }

    if (activeItem.id === this.APPLE_CATEGORY_ID) {
      this.navigationService.goToBrandStore(this.APPLE_BRAND_ID);
    } else {
      this.navigationService.goToCategoryPage(activeItem.id, {
        data: JSON.stringify([activeItem]),
      });
    }

    this.closeMenu();
  }

  clickNavItem(...segments: INavigationTree[]): void {
    if (!segments.length) return;

    const firstSegment = segments[0];

    // Handle Apple category navigation using centralized list
    if (firstSegment.id === this.APPLE_CATEGORY_ID) {
      this.navigationService.goToBrandStore(this.APPLE_BRAND_ID);
    } else if (this.headerMenuService.getAllAppleCategoryIds().includes(firstSegment.id)) {
      this.headerMenuService.navigateToAppleCategory(firstSegment.id, this.navigationService, segments);
    } else {
      this.navigationService.goToCategoryPage(firstSegment.id, {
        data: JSON.stringify(segments),
      });
    }

    this.closeMenu();
  }

  closeMenu(): void {
    this.navigationMenuMobileService.emitCloseEvent();
  }
}
